import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import { useTranslation } from "../../../i18n";
import { useTeam } from "../TeamLayout";
import { useTableViewContactsQuery } from "../../../__generated__/types-and-hooks";
import { AspectRatio, Breadcrumbs, Button, Input, Link } from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import TableView from "./TableView";
import { useCallback, useEffect, useMemo, useState } from "react";
import HomeIcon from "@mui/icons-material/Home";
import { Link as RouterLink } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ContactAddModal from "./ContactAddModal";
import Fuse from "fuse.js";

export default function Contacts() {
  const t = useTranslation();
  const teamCtx = useTeam();
  const contactsListQuery = useTableViewContactsQuery({
    variables: { teamId: teamCtx.team.id },
  });

  const [contactAddModal, setContactAddModal] = useState<boolean>(false);
  const handleCreateContact = useCallback(() => {
    setContactAddModal(true);
  }, [setContactAddModal]);

  const contacts =
    contactsListQuery.data && contactsListQuery.data.contact_list?.contacts;

  const [search, setSearch] = useState<string | null>(null);
  const [contactsFiltered, setContactsFiltered] = useState<
    typeof contacts | undefined
  >(undefined);

  const fuse = useMemo(() => {
    if (!contacts) return null;

    return new Fuse(contacts, {
      keys: [
        { name: "uniqueId", weight: 1 },
        { name: "name", weight: 0.8 },
        { name: "contactMethods.value", weight: 0.5 },
        { name: "addressLine1", weight: 0.3 },
        { name: "addressLine2", weight: 0.2 },
        { name: "city", weight: 0.1 },
        { name: "state", weight: 0.1 },
        { name: "contryCode", weight: 0.1 },
        { name: "suppliers.name", weight: 0.1 },
      ],
      threshold: 0.2, // 0 is exact, 1 is very fuzzy
      includeScore: true,
    });
  }, [contacts]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setContactsFiltered(
        fuse && search && search.trim() !== ""
          ? fuse.search(search.trim()).map((result) => result.item)
          : contacts
      );
    }, 500);

    return () => clearTimeout(handler); // cleanup on every keystroke
  }, [search, contacts, fuse]);

  return (
    <>
      <Breadcrumbs
        aria-label="breadcrumbs"
        sx={{
          "--Breadcrumbs-gap": "4px",
          m: 1,
        }}
        size="sm"
      >
        <Link component={RouterLink} color="neutral" to="/">
          <HomeIcon />
        </Link>
        <Link
          component={RouterLink}
          color="neutral"
          to={`/${teamCtx.team.slug}`}
        >
          {teamCtx.team.name}
        </Link>
        <Typography level="title-sm">{t("Contacts")}</Typography>
      </Breadcrumbs>
      <Box sx={{ display: "flex", m: 2 }} gap={1}>
        <Input
          type="text"
          placeholder={t("Search...")}
          startDecorator={<SearchIcon />}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Box sx={{ flex: 1 }}></Box>
        <Button
          startDecorator={<AddIcon />}
          variant="outlined"
          onClick={handleCreateContact}
        >
          {t("Add")}
        </Button>
      </Box>
      <Box
        sx={{
          gap: 2,
          px: 2,
          display: "flex",
          flexDirection: "column",
          height: "calc(100% - 120px)",
        }}
      >
        {contacts && contacts.length === 0 && (
          <>
            <Typography color="neutral" level="body-md">
              {t(
                "Manage all your contacts in one place to streamline your supply chain operations. Add contacts manually or connect with integrations like Anvyl or NetSuite."
              )}
              <br />
              <Link
                component={RouterLink}
                to={`/${teamCtx.team.slug}/settings/integrations`}
              >
                {t("Settings/Integrations")}
              </Link>
            </Typography>
            <AspectRatio
              variant="plain"
              ratio={1}
              objectFit="contain"
              maxHeight={"50vh"}
              sx={{ bgcolor: "transparent" }}
            >
              <img
                alt="No contacts yet"
                src="/images/contacts_empty_state.png"
              />
            </AspectRatio>
            <Box
              sx={{
                textAlign: "center",
              }}
            >
              <Button
                startDecorator={<AddIcon />}
                sx={{ mb: 2 }}
                variant="outlined"
                onClick={handleCreateContact}
              >
                {t("Add New Contact")}
              </Button>
            </Box>
          </>
        )}
        <TableView
          loading={contactsListQuery.loading}
          contacts={contactsFiltered ?? contacts ?? []}
          onUpdate={() => contactsListQuery.refetch()}
        />
      </Box>
      <ContactAddModal
        open={contactAddModal}
        onClose={() => setContactAddModal(false)}
      />
    </>
  );
}
